import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Entry } from 'contentful';
import { NewsPage } from 'src/app/model/page/news-page';
import { ContentfulService } from 'src/app/service/contentful.service';
import { ModelValidator } from 'src/app/validator/model-validator';
import htmlTitle from 'src/assets/json/redirects/html-title.json';

@Component({
  selector: 'app-news-page',
  templateUrl: './news-page.component.html',
  styleUrls: ['./news-page.component.scss']
})
export class NewsPageComponent implements OnInit, OnDestroy {
  @Input() selectedYearIndex: number = 0;

  newsPage: NewsPage = {} as NewsPage;
  newsYears: string[] = [];
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  private queryParamsSubscription: Subscription = new Subscription();

  constructor(private router: Router, private route: ActivatedRoute, private contentfulService: ContentfulService) {}

  ngOnInit(): void {
    htmlTitle.filter((title) => {
      if (title.page === 'news') {
        this.newsPage.htmlTitle = title.htmlTitle;
      }
    });

    this.newsPage.newsUpdateCards = [];
    this.loadNewsContent();
  }

  private loadNewsContent(): void {
    this.isLoading = true;
    this.hasError = false;
  
    const query = { 'fields.title': 'NEWS' };
    this.contentfulService.getPageContentResponse(query)
      .then((newsPageEntries) => {
        this.mapHtmlTitle(newsPageEntries.items);
        this.mapBanner(newsPageEntries.includes?.Entry || []);
        this.mapCards(newsPageEntries.includes?.Entry || []);
        this.mapTabsYear();
        this.applyYearFromQueryParam();
        this.setSelectedYearFromQuery();
        this.isLoading = false;
      })
      .catch((error) => {
        this.hasError = true;
        this.errorMessage = 'Failed to load news content. Please try again later.';
        this.isLoading = false;
        this.newsYears = [];
        this.selectedYearIndex = 0;
        this.newsPage.newsUpdateCards = [];
      });
  }

  private applyYearFromQueryParam(): void {
    const yearParam = this.route.snapshot.queryParams['y'] 
      || this.extractYearFromUrl();
    if (yearParam && this.newsYears.length > 0) {
      const yearIndex = this.newsYears.findIndex(year => year === yearParam);
      this.selectedYearIndex = yearIndex >= 0 ? yearIndex : 0;
    }
  }

  private extractYearFromUrl(): string | null {
    const url = decodeURIComponent(this.router.url);
    const match = url.match(/[?&]y=([^&]+)/);
    return match ? match[1] : null;
  }

  private mapHtmlTitle(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      this.newsPage.htmlTitle = entry.fields.htmlTitle;
    });
  }

  private mapCards(newsPageEntries: Entry<any>[]) {
    newsPageEntries
      .filter((entry) => ModelValidator.isCard(entry))
      .forEach((entry) => {
        this.mapEntryToNewsUpdateCard(entry);
      });
  }

  private mapBanner(newsPageEntries: Entry<any>[]) {
    newsPageEntries
      .filter((entry) => ModelValidator.isBanner(entry))
      .forEach((entry) => {
        this.mapEntryToBanner(entry);
      });
  }

  private mapEntryToBanner(entry: Entry<any>) {
    let imageDetails: any = entry.fields['image'];
    let videoDetails: any = entry.fields['video'];
    this.newsPage.banner = {
      title: entry.fields['title'] as string,
      subTitle: entry.fields['subTitle'] as string,
      image: imageDetails.fields.file.url as string,
      video: videoDetails?.fields?.file?.url as string
    };
  }

  private mapEntryToNewsUpdateCard(entry: Entry<any>) {
    let leftImageDetails: any = entry.fields['leftImage'];
    let bodyDetails: any = entry.fields['body'];
    let footIconDetails: any = entry.fields['footIcon'];
    this.newsPage.newsUpdateCards.push({
      title: entry.fields['title'] as string,
      order: entry.fields['order'] as number,
      headText: entry.fields['headText'] as string,
      leftImage: leftImageDetails.fields.file.url,
      body: bodyDetails.content[0]?.content[0]?.value,
      footIcon: footIconDetails.fields.file.url,
      footText: entry.fields['footText'] as string,
      url: entry.fields['url'] as string
    });
  }

  mapTabsYear() {
    this.newsYears = this.newsPage.newsUpdateCards.map((newsUpdateCard) => {
      return newsUpdateCard.headText;
    });
    this.newsYears = [...new Set(this.newsYears)].sort().reverse();
    this.newsYears.push(this.newsYears.shift() || '');
  }

  private setSelectedYearFromQuery(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      const yearParam = params['y'];
      if (yearParam && this.newsYears.length > 0) {
        const yearIndex = this.newsYears.findIndex(year => year === yearParam);
        this.selectedYearIndex = yearIndex >= 0 ? yearIndex : 0;
      } else {
        this.selectedYearIndex = 0;
      }
    });
  }

  onYearTabClick(yearIndex: number): void {
    this.selectedYearIndex = yearIndex;
    this.router.navigate(['/news'], { queryParams: { y: this.newsYears[yearIndex] } });
  }
  
  ngOnDestroy(): void {
    this.queryParamsSubscription.unsubscribe();
  }
}
